import { useState, useEffect, useRef, useMemo } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { toast } from 'react-toastify'
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import { FaFileExcel, FaFilePdf } from "react-icons/fa"
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from "axios"

const LocationBillingHistory = () => {
    const [history, setHistory] = useState([])
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([])
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
    const [isLoading, setIsLoading] = useState(false)
    const [title, setTitle] = useState('')
    const gridRef = useRef();

    const navigate = useNavigate()
    const location = useLocation()

    useEffect(() => {
        if (location.state){
            const {data} = location.state
            setTitle(`${data.full_name} -- ${data.remark} --${data.location_id}(${data.village_name}/${data.zone_name}) Billing History as ${date}`)
            fetchData({public_id: data.public_id, previous_id: data.previous_id})
        }
    }, [])

    const [columnDefs] = useState([
        {
            headerName: "Date",
            field: "date",
            sortable: true,
            filter: 'agDateColumnFilter',
            filterParams: {
                comparator: (filterDate, cellValue) => {
                  const cellDate = new Date(cellValue);
                  cellDate.setHours(0, 0, 0, 0);
                  filterDate.setHours(0, 0, 0, 0);

                  if (cellDate < filterDate) return -1;
                  if (cellDate > filterDate) return 1;
                  return 0;
                },
            },
        },
        {
            headerName: "Meter",
            field: "meter",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Remark",
            field: "remark",
            sortable: true,
            filter: true,
        },
        {
            headerName: "PReading",
            field: "preading",
            sortable: true,
            filter: true,
        },
        {
            headerName: "CReading",
            field: "creading",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Difference",
            field: "difference",
            sortable: true,
            filter: true,
        },
        {
            headerName: "PBalance",
            field: "pbalance",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Debit",
            field: "debit",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Total",
            field: "total",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Credit",
            cellRenderer: (e) => {
              if (!e.data) return null;
              if (e.node.rowPinned) return `${e.data.credit}`;
              if (e.data.credit < 1) return `${e.data.credit}`;
              return <Link to="/location-monthly-payment-detail" state={{
                    payload: {
                        public_id: e.data.public_id,
                        year: e.data.year,
                        month: e.data.month,
                    }
                }}>{e.data.credit}</Link>},
            sortable: false,
            filter: false,
        },
        {
            headerName: "Discount",
            field: "discount",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Balance",
            field: "balance",
            sortable: true,
            filter: true,
        },
    ]);

    const fetchData = async (payload) => {
        setIsLoading(true)
        try {
            const response = await Server.get(`/locations/billing-history?public_id=${payload.public_id}`)
            setHistory(response?.data);

            if (payload?.previous_id !=  null) {
                const prev_history = await axios.get(`https://v1.kobciyeubs.com/Customer/${payload?.previous_id}/BillingHistoryAPI`)
                if (prev_history.status === 200){
                    setHistory(prev => [
                        ...prev, // Keep the previous history
                        ...prev_history.data.map(history => ({
                            date: history.date,
                            debit: history.bill,
                            preading: parseInt(history.reading) - parseInt(history.difference),
                            creading: parseInt(history.reading),
                            pbalance: history.previous_balance,
                            credit: history.paid,
                            discount: history.discount,
                            balance: history.balance,
                            difference: history.difference,
                            total: history.total_bill,
                        }))
                    ]);
                }
            }
            setIsLoading(false);

            const output = response?.data.reduce((data, row) => {
                data.difference += isNaN(row.difference) ? 0 : parseFloat(row.difference);
                data.debit += parseFloat(row.debit);
                data.credit += parseFloat(row.credit);
                data.discount += parseFloat(row.discount);
                return data;
            }, {difference: 0, debit: 0.00, credit: 0.00, discount: 0.00});

            setPinnedBottomRowData([
                {
                    difference: parseFloat(output.difference).toFixed(0),
                    debit: parseFloat(output.debit).toFixed(2),
                    credit: parseFloat(output.credit).toFixed(2),
                    discount: parseFloat(output.discount).toFixed(2),
                    balance: (output.debit - (output.credit + output.discount)).toFixed(2),
                }
            ]);
        } catch (error) {
            setIsLoading(false);
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error.response.data.detail);
                navigate('/');
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to fetch billing history')
            }
        }
    }


    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    let gridApi

    const onGridReady=(params)=>{
        gridApi=params.api
    }

    const onExportClick=() => {
        gridApi.exportDataAsCsv({ fileName: `${title}.csv` })
    }

    const exportPDF = () => {
        const doc = new jsPDF('landscape');

        // Fetch the grid data
        const gridApi = gridRef.current.api;
        const rowData = [];
        gridApi.forEachNode((node) => rowData.push(node.data));

        // Prepare column headers and rows
        const columns = columnDefs.map((col) => col.headerName);
        const rows = rowData.map((row) => columns.map((col) => row[col.toLowerCase()]));

        // Add title
        doc.text(`${title}`, 14, 10);

        // Add the autoTable
        doc.autoTable({
          head: [columns],
          body: rows,
          startY: 15,
          styles: { fontSize: 10 },
          theme: 'grid',
          margin: { left: 5, right: 5 },
          pageBreak: 'auto',
          tableWidth: 'wrap',
        });

        // Save the PDF
        doc.save(`${title}.pdf`);
      };

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <div className="row">
            <div className="col-md-12">
                <button onClick={()=>onExportClick()} className="btn btn-success   mb-2 mr-4" style={{float: 'right', marginLeft: '10px'}}><FaFileExcel/> &nbsp;Export</button>
                <button onClick={()=>exportPDF()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFilePdf/> &nbsp; Export</button>
            </div>
        </div>
        <div className="card">
        <div className="card-header p-2 bg-white text-black d-flex justify-content-center">
                <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            defaultColDef={defaultColDef}
                            suppressMenuHide={true}

                            rowData={history}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            pagination={false}
                            onGridReady={onGridReady}

                            resizable={true}
                            animateRows={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}


                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{""}
                    </div>
                    </div>
                </div>
        </div>
        </div>
      <br/>
    </>
  )
}

export default LocationBillingHistory